/** El calendario: un mes por pantalla, cada fila en el día de su fecha. Se arrastra una fila a
 * otro día para cambiarle la fecha, y «＋» en un día crea una fila ya con esa fecha. */
import { useMemo, useState } from 'react'

import type { AccionesDeVista } from './BaseVista'
import { opcionDe } from './logica'
import type { FechaValor, Fila } from './tipos'

const DIAS = ['lun', 'mar', 'mié', 'jue', 'vie', 'sáb', 'dom']

/** El día como texto `AAAA-MM-DD`, con la hora local: `toISOString` lo movería un día de noche. */
function iso(d: Date): string {
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const dia = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${dia}`
}

let arrastrando: number | null = null

export function Calendario(p: AccionesDeVista) {
  const { esquema, vista, filas, editable } = p
  const propFecha = esquema.properties.find((c) => c.id === vista.date_by && c.type === 'date')
  const [mes, setMes] = useState(() => {
    const hoy = new Date()
    return new Date(hoy.getFullYear(), hoy.getMonth(), 1)
  })
  const [sobre, setSobre] = useState<string | null>(null)
  // Los eventos toman el color de la primera propiedad de estado u opciones que tenga la base.
  const propColor = esquema.properties.find((c) => c.type === 'select' || c.type === 'status')
  const tonoDe = (f: Fila) => {
    const o = propColor ? opcionDe(propColor, f.props[propColor.id]) : undefined
    return o ? ` tono--${o.color}` : ''
  }

  const dias = useMemo(() => {
    // Lunes como primer día, que es como se lee un calendario aquí.
    const primero = new Date(mes.getFullYear(), mes.getMonth(), 1)
    const desfase = (primero.getDay() + 6) % 7
    const inicio = new Date(primero)
    inicio.setDate(1 - desfase)
    const total = Math.ceil((desfase + new Date(mes.getFullYear(), mes.getMonth() + 1, 0).getDate()) / 7) * 7
    return Array.from({ length: total }, (_, i) => {
      const d = new Date(inicio)
      d.setDate(inicio.getDate() + i)
      return d
    })
  }, [mes])

  if (!propFecha) {
    return (
      <div className="bd__vacio">
        Este calendario necesita una propiedad de tipo Fecha para colocar las filas.
        {editable && (
          <select aria-label="Colocar por" value=""
                  onChange={(e) => p.alCambiarVista({ ...vista, date_by: e.target.value })}>
            <option value="">Elige una propiedad…</option>
            {esquema.properties.filter((c) => c.type === 'date').map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        )}
      </div>
    )
  }

  const porDia = new Map<string, Fila[]>()
  const sinFecha: Fila[] = []
  for (const f of filas) {
    const inicio = (f.props[propFecha.id] as FechaValor | undefined)?.start?.slice(0, 10)
    if (!inicio) sinFecha.push(f)
    else porDia.set(inicio, [...(porDia.get(inicio) ?? []), f])
  }

  const hoy = iso(new Date())
  const largo = mes.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
  const nombreMes = largo.charAt(0).toUpperCase() + largo.slice(1)
  const mover = (delta: number) => setMes(new Date(mes.getFullYear(), mes.getMonth() + delta, 1))

  const soltar = (dia: string) => {
    const id = arrastrando
    arrastrando = null
    setSobre(null)
    if (id != null) p.alCambiarFila(id, { props: { [propFecha.id]: { start: dia } } })
  }

  return (
    <div className="calendario">
      <div className="calendario__barra">
        <button className="boton boton--plano" onClick={() => mover(-1)} aria-label="Mes anterior">‹</button>
        <strong className="calendario__mes">{nombreMes}</strong>
        <button className="boton boton--plano" onClick={() => mover(1)} aria-label="Mes siguiente">›</button>
        <button className="boton" onClick={() => setMes(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}>
          Hoy
        </button>
      </div>

      <div className="calendario__rejilla" role="grid">
        {DIAS.map((d) => <div key={d} className="calendario__dia-semana" role="columnheader">{d}</div>)}
        {dias.map((d) => {
          const clave = iso(d)
          const fuera = d.getMonth() !== mes.getMonth()
          const dentro = porDia.get(clave) ?? []
          return (
            <div
              key={clave}
              role="gridcell"
              className={`calendario__celda${fuera ? ' calendario__celda--fuera' : ''}${clave === hoy ? ' calendario__celda--hoy' : ''}${sobre === clave ? ' calendario__celda--sobre' : ''}`}
              onDragOver={(e) => { if (arrastrando != null && editable) { e.preventDefault(); setSobre(clave) } }}
              onDragLeave={() => setSobre(null)}
              onDrop={(e) => { e.preventDefault(); soltar(clave) }}
            >
              <div className="calendario__cabecera">
                <span className="calendario__numero">{d.getDate()}</span>
                {editable && (
                  <button className="calendario__mas" aria-label={`Nueva fila el ${clave}`}
                          onClick={() => p.alNuevaFila({ [propFecha.id]: { start: clave } })}>＋</button>
                )}
              </div>
              {dentro.map((f) => (
                <button
                  key={f.id}
                  className={`calendario__evento${tonoDe(f)}`}
                  draggable={editable}
                  onDragStart={(e) => { arrastrando = f.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(f.id)) }}
                  onDragEnd={() => { arrastrando = null; setSobre(null) }}
                  onClick={() => p.alAbrir(f.id)}
                  title={f.title || 'Sin título'}
                >
                  {f.title || 'Sin título'}
                </button>
              ))}
            </div>
          )
        })}
      </div>

      {sinFecha.length > 0 && (
        <div className="calendario__sin-fecha">
          <div className="panel__titulo">Sin fecha · {sinFecha.length}</div>
          {sinFecha.map((f) => (
            <button key={f.id} className={`calendario__evento${tonoDe(f)}`} draggable={editable}
                    onDragStart={(e) => { arrastrando = f.id; e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', String(f.id)) }}
                    onDragEnd={() => { arrastrando = null }}
                    onClick={() => p.alAbrir(f.id)}>
              {f.title || 'Sin título'}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
